/**
 * @file Colour palette
 * @description The colours a gardener may choose from, written out in full.
 *
 * Tailwind only ships the classes it can literally see in the source, so a
 * colour assembled at runtime from a name in the database would compile to
 * nothing and render unstyled. Every class a user can pick therefore appears
 * here as plain text. That is also the safety rail: a colour key that arrived
 * from anywhere — the database, an API response, a typo — can only ever
 * resolve to one of these, or to the fallback.
 *
 * This file is generated shape-wise but edited by hand is fine; the point is
 * that the strings are literal.
 *
 * Config, not code: which colours a gardener may choose from is an admin's
 * decision. The class strings must stay in source for Tailwind, but the list
 * offered (COLOR_KEYS) and the default could come from the database. @kept K7
 *
 * Used by: the profile's colour picker (ColorPicker, StageSettings,
 * NoteShortcutSettings), the note pills (QuickNoteChips), the stage colours
 * (stageThemes) and the outlook's colours where a verdict has no theme
 * token (utils/outlookSeverity). A stage's or pill's color_key on the
 * server is one of COLOR_KEYS.
 *
 * @module theme/colorPalette
 */

import { ColorTheme } from '../types/theme';

/**
 * One colour, in every mode the app renders.
 *
 * Wider than ColorTheme by one thing: a single swatch class for the picker,
 * where a colour has to be shown as itself rather than as a styled thing.
 * (A per-entry "garden" block used to sit here too; nothing ever read it,
 * and the theme it was for is gone.)
 */
export interface PaletteEntry extends ColorTheme {
  /** A solid background class showing the colour itself, for the picker's dot. */
  swatch: string;
}

/** Every colour a stage or a note shortcut may be painted. */
export const COLOR_KEYS = [
  'gray',
  'red',
  'orange',
  'amber',
  'yellow',
  'lime',
  'green',
  'emerald',
  'teal',
  'cyan',
  'sky',
  'blue',
  'indigo',
  'violet',
  'purple',
  'fuchsia',
  'pink',
  'rose',
] as const;

/** One of the colour names a gardener may choose. */
export type ColorKey = typeof COLOR_KEYS[number];

/** Used when a colour key is missing or unrecognised. */
export const DEFAULT_COLOR_KEY: ColorKey = 'green';

/** Every choosable colour, with its classes for light and dark mode and its swatch. */
export const COLOR_THEMES: Record<ColorKey, PaletteEntry> = {
  gray: {
    light: {
      bg: 'bg-gray-50',
      text: 'text-gray-800',
      border: 'border-gray-600',
      badge: 'bg-gray-100 text-gray-800',
      icon: 'text-gray-600',
    },
    dark: {
      bg: 'dark:bg-gray-900/20',
      text: 'dark:text-gray-200',
      border: 'dark:border-gray-700',
      badge: 'dark:bg-gray-800 dark:text-gray-200',
      icon: 'dark:text-gray-400',
    },
    /** A single filled circle for the picker, where the name means nothing. */
    swatch: 'bg-gray-500',
  },
  red: {
    light: {
      bg: 'bg-red-50',
      text: 'text-red-800',
      border: 'border-red-600',
      badge: 'bg-red-100 text-red-800',
      icon: 'text-red-600',
    },
    dark: {
      bg: 'dark:bg-red-900/20',
      text: 'dark:text-red-200',
      border: 'dark:border-red-700',
      badge: 'dark:bg-red-800 dark:text-red-200',
      icon: 'dark:text-red-400',
    },
    /** A single filled circle for the picker, where the name means nothing. */
    swatch: 'bg-red-500',
  },
  orange: {
    light: {
      bg: 'bg-orange-50',
      text: 'text-orange-800',
      border: 'border-orange-600',
      badge: 'bg-orange-100 text-orange-800',
      icon: 'text-orange-600',
    },
    dark: {
      bg: 'dark:bg-orange-900/20',
      text: 'dark:text-orange-200',
      border: 'dark:border-orange-700',
      badge: 'dark:bg-orange-800 dark:text-orange-200',
      icon: 'dark:text-orange-400',
    },
    /** A single filled circle for the picker, where the name means nothing. */
    swatch: 'bg-orange-500',
  },
  amber: {
    light: {
      bg: 'bg-amber-50',
      text: 'text-amber-800',
      border: 'border-amber-600',
      badge: 'bg-amber-100 text-amber-800',
      icon: 'text-amber-600',
    },
    dark: {
      bg: 'dark:bg-amber-900/20',
      text: 'dark:text-amber-200',
      border: 'dark:border-amber-700',
      badge: 'dark:bg-amber-800 dark:text-amber-200',
      icon: 'dark:text-amber-400',
    },
    /** A single filled circle for the picker, where the name means nothing. */
    swatch: 'bg-amber-500',
  },
  yellow: {
    light: {
      bg: 'bg-yellow-50',
      text: 'text-yellow-800',
      border: 'border-yellow-600',
      badge: 'bg-yellow-100 text-yellow-800',
      icon: 'text-yellow-600',
    },
    dark: {
      bg: 'dark:bg-yellow-900/20',
      text: 'dark:text-yellow-200',
      border: 'dark:border-yellow-700',
      badge: 'dark:bg-yellow-800 dark:text-yellow-200',
      icon: 'dark:text-yellow-400',
    },
    /** A single filled circle for the picker, where the name means nothing. */
    swatch: 'bg-yellow-500',
  },
  lime: {
    light: {
      bg: 'bg-lime-50',
      text: 'text-lime-800',
      border: 'border-lime-600',
      badge: 'bg-lime-100 text-lime-800',
      icon: 'text-lime-600',
    },
    dark: {
      bg: 'dark:bg-lime-900/20',
      text: 'dark:text-lime-200',
      border: 'dark:border-lime-700',
      badge: 'dark:bg-lime-800 dark:text-lime-200',
      icon: 'dark:text-lime-400',
    },
    /** A single filled circle for the picker, where the name means nothing. */
    swatch: 'bg-lime-500',
  },
  green: {
    light: {
      bg: 'bg-green-50',
      text: 'text-green-800',
      border: 'border-green-600',
      badge: 'bg-green-100 text-green-800',
      icon: 'text-green-600',
    },
    dark: {
      bg: 'dark:bg-green-900/20',
      text: 'dark:text-green-200',
      border: 'dark:border-green-700',
      badge: 'dark:bg-green-800 dark:text-green-200',
      icon: 'dark:text-green-400',
    },
    /** A single filled circle for the picker, where the name means nothing. */
    swatch: 'bg-green-500',
  },
  emerald: {
    light: {
      bg: 'bg-emerald-50',
      text: 'text-emerald-800',
      border: 'border-emerald-600',
      badge: 'bg-emerald-100 text-emerald-800',
      icon: 'text-emerald-600',
    },
    dark: {
      bg: 'dark:bg-emerald-900/20',
      text: 'dark:text-emerald-200',
      border: 'dark:border-emerald-700',
      badge: 'dark:bg-emerald-800 dark:text-emerald-200',
      icon: 'dark:text-emerald-400',
    },
    /** A single filled circle for the picker, where the name means nothing. */
    swatch: 'bg-emerald-500',
  },
  teal: {
    light: {
      bg: 'bg-teal-50',
      text: 'text-teal-800',
      border: 'border-teal-600',
      badge: 'bg-teal-100 text-teal-800',
      icon: 'text-teal-600',
    },
    dark: {
      bg: 'dark:bg-teal-900/20',
      text: 'dark:text-teal-200',
      border: 'dark:border-teal-700',
      badge: 'dark:bg-teal-800 dark:text-teal-200',
      icon: 'dark:text-teal-400',
    },
    /** A single filled circle for the picker, where the name means nothing. */
    swatch: 'bg-teal-500',
  },
  cyan: {
    light: {
      bg: 'bg-cyan-50',
      text: 'text-cyan-800',
      border: 'border-cyan-600',
      badge: 'bg-cyan-100 text-cyan-800',
      icon: 'text-cyan-600',
    },
    dark: {
      bg: 'dark:bg-cyan-900/20',
      text: 'dark:text-cyan-200',
      border: 'dark:border-cyan-700',
      badge: 'dark:bg-cyan-800 dark:text-cyan-200',
      icon: 'dark:text-cyan-400',
    },
    /** A single filled circle for the picker, where the name means nothing. */
    swatch: 'bg-cyan-500',
  },
  sky: {
    light: {
      bg: 'bg-sky-50',
      text: 'text-sky-800',
      border: 'border-sky-600',
      badge: 'bg-sky-100 text-sky-800',
      icon: 'text-sky-600',
    },
    dark: {
      bg: 'dark:bg-sky-900/20',
      text: 'dark:text-sky-200',
      border: 'dark:border-sky-700',
      badge: 'dark:bg-sky-800 dark:text-sky-200',
      icon: 'dark:text-sky-400',
    },
    /** A single filled circle for the picker, where the name means nothing. */
    swatch: 'bg-sky-500',
  },
  blue: {
    light: {
      bg: 'bg-blue-50',
      text: 'text-blue-800',
      border: 'border-blue-600',
      badge: 'bg-blue-100 text-blue-800',
      icon: 'text-blue-600',
    },
    dark: {
      bg: 'dark:bg-blue-900/20',
      text: 'dark:text-blue-200',
      border: 'dark:border-blue-700',
      badge: 'dark:bg-blue-800 dark:text-blue-200',
      icon: 'dark:text-blue-400',
    },
    /** A single filled circle for the picker, where the name means nothing. */
    swatch: 'bg-blue-500',
  },
  indigo: {
    light: {
      bg: 'bg-indigo-50',
      text: 'text-indigo-800',
      border: 'border-indigo-600',
      badge: 'bg-indigo-100 text-indigo-800',
      icon: 'text-indigo-600',
    },
    dark: {
      bg: 'dark:bg-indigo-900/20',
      text: 'dark:text-indigo-200',
      border: 'dark:border-indigo-700',
      badge: 'dark:bg-indigo-800 dark:text-indigo-200',
      icon: 'dark:text-indigo-400',
    },
    /** A single filled circle for the picker, where the name means nothing. */
    swatch: 'bg-indigo-500',
  },
  violet: {
    light: {
      bg: 'bg-violet-50',
      text: 'text-violet-800',
      border: 'border-violet-600',
      badge: 'bg-violet-100 text-violet-800',
      icon: 'text-violet-600',
    },
    dark: {
      bg: 'dark:bg-violet-900/20',
      text: 'dark:text-violet-200',
      border: 'dark:border-violet-700',
      badge: 'dark:bg-violet-800 dark:text-violet-200',
      icon: 'dark:text-violet-400',
    },
    /** A single filled circle for the picker, where the name means nothing. */
    swatch: 'bg-violet-500',
  },
  purple: {
    light: {
      bg: 'bg-purple-50',
      text: 'text-purple-800',
      border: 'border-purple-600',
      badge: 'bg-purple-100 text-purple-800',
      icon: 'text-purple-600',
    },
    dark: {
      bg: 'dark:bg-purple-900/20',
      text: 'dark:text-purple-200',
      border: 'dark:border-purple-700',
      badge: 'dark:bg-purple-800 dark:text-purple-200',
      icon: 'dark:text-purple-400',
    },
    /** A single filled circle for the picker, where the name means nothing. */
    swatch: 'bg-purple-500',
  },
  fuchsia: {
    light: {
      bg: 'bg-fuchsia-50',
      text: 'text-fuchsia-800',
      border: 'border-fuchsia-600',
      badge: 'bg-fuchsia-100 text-fuchsia-800',
      icon: 'text-fuchsia-600',
    },
    dark: {
      bg: 'dark:bg-fuchsia-900/20',
      text: 'dark:text-fuchsia-200',
      border: 'dark:border-fuchsia-700',
      badge: 'dark:bg-fuchsia-800 dark:text-fuchsia-200',
      icon: 'dark:text-fuchsia-400',
    },
    /** A single filled circle for the picker, where the name means nothing. */
    swatch: 'bg-fuchsia-500',
  },
  pink: {
    light: {
      bg: 'bg-pink-50',
      text: 'text-pink-800',
      border: 'border-pink-600',
      badge: 'bg-pink-100 text-pink-800',
      icon: 'text-pink-600',
    },
    dark: {
      bg: 'dark:bg-pink-900/20',
      text: 'dark:text-pink-200',
      border: 'dark:border-pink-700',
      badge: 'dark:bg-pink-800 dark:text-pink-200',
      icon: 'dark:text-pink-400',
    },
    /** A single filled circle for the picker, where the name means nothing. */
    swatch: 'bg-pink-500',
  },
  rose: {
    light: {
      bg: 'bg-rose-50',
      text: 'text-rose-800',
      border: 'border-rose-600',
      badge: 'bg-rose-100 text-rose-800',
      icon: 'text-rose-600',
    },
    dark: {
      bg: 'dark:bg-rose-900/20',
      text: 'dark:text-rose-200',
      border: 'dark:border-rose-700',
      badge: 'dark:bg-rose-800 dark:text-rose-200',
      icon: 'dark:text-rose-400',
    },
    /** A single filled circle for the picker, where the name means nothing. */
    swatch: 'bg-rose-500',
  },
};

/**
 * Resolves a colour key of unknown provenance to a theme.
 *
 * @param key - Anything; only a known key is honoured.
 * @returns The matching theme, or the default one.
 */
export function themeForColorKey(key: string | null | undefined): PaletteEntry {
  if (isColorKey(key)) return COLOR_THEMES[key];
  return COLOR_THEMES[DEFAULT_COLOR_KEY];
}

/**
 * True when the key is one this app can actually paint.
 *
 * Its own keys only. `key in COLOR_THEMES` also said yes to anything on
 * Object.prototype ("toString", "constructor"), which then resolved to a
 * function rather than a palette entry and broke whatever drew it.
 */
export function isColorKey(key: string | null | undefined): key is ColorKey {
  return !!key && Object.prototype.hasOwnProperty.call(COLOR_THEMES, key);
}
